import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

export interface DropdownOption {
  value: string;
  label: string;
  /** Optional dot color class (e.g. `bg-emerald-500`) shown before the label. */
  dot?: string;
  /** Optional icon rendered before the label. */
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface DropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  placeholder?: string;
  showPlaceholderWhenEmpty?: boolean;
  /** Renders a small label chip inside the trigger before the value. */
  leadingLabel?: string;
  /** Width of the trigger. Defaults to `min-w-[160px]`. */
  className?: string;
  disabled?: boolean;
  /** Trigger height. */
  size?: "sm" | "md";
  align?: "start" | "center" | "end";
  /**
   * Visual style of the trigger.
   * - `pill` (default): rounded card-style pill — best for filter bars / toolbars.
   * - `form`: matches the in-modal form-input look (subtle bg, full-width).
   */
  variant?: "pill" | "form";
}

const SIZE_CLASS = {
  sm: "h-[38px] text-sm px-3",
  md: "h-[38px] text-sm px-3.5",
} as const;

const VARIANT_CLASS = {
  pill: "rounded-lg border border-border bg-card text-foreground hover:border-indigo-400/60 dark:hover:border-indigo-400/40 focus-visible:border-indigo-500 dark:focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500/20",
  form: "rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] bg-[#f5f7fb] dark:bg-white/[0.04] text-[#0f172a] dark:text-white hover:border-indigo-400/60 dark:hover:border-indigo-400/40 focus-visible:border-indigo-500 dark:focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500/10 w-full",
} as const;

/**
 * Single dropdown component used everywhere in the app.
 *
 * Looks identical in light & dark themes via shadcn/Tailwind tokens
 * (`bg-card`, `border-border`, `text-foreground`, `bg-popover`, etc.).
 *
 * Built on Radix Select so it gets keyboard navigation, focus trap,
 * portal positioning, and a11y for free.
 */
/**
 * Radix Select forbids `value=""` on items (it reserves "" for "no selection").
 * Many call sites use `""` to mean "All / Any" AS A SELECTABLE OPTION. We map
 * empty string ↔ this sentinel token internally so callers can keep using `""`.
 *
 * If the consumer's value is `""` but NO option uses `""`, treat it as
 * "unselected" and let Radix show the placeholder.
 */
const EMPTY_VALUE_TOKEN = "__dropdown_empty__";
const fromRadix = (v: string) => (v === EMPTY_VALUE_TOKEN ? "" : v);

/** Selected value that reveals the full text in a themed tooltip — only when truncated. */
function SelectedLabel({ label }: { label: string }) {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [open, setOpen] = React.useState(false);
  const isOverflowing = () => {
    const el = ref.current;
    return !!el && el.scrollWidth > el.clientWidth + 1;
  };
  return (
    <Tooltip
      open={open}
      onOpenChange={(next) => setOpen(next ? isOverflowing() : false)}
    >
      <TooltipTrigger asChild>
        <span ref={ref} className="truncate">
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={6} className="max-w-xs break-words">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

export function Dropdown({
  value,
  onChange,
  options,
  placeholder = "Select…",
  showPlaceholderWhenEmpty = false,
  leadingLabel,
  className = "",
  disabled,
  size = "md",
  align = "start",
  variant = "pill",
}: DropdownProps) {
  const hasEmptyOption = options.some((o) => o.value === "");
  const selected = options.find((o) => o.value === value);
  // Show the placeholder (not the "None" label) for an empty value when the
  // caller opts in — the empty option stays in the list for reselection.
  const showEmptyAsPlaceholder = showPlaceholderWhenEmpty && value === "";
  // If value is "" and no option uses "", treat as unselected -> undefined so
  // Radix renders the placeholder. Otherwise translate "" to the sentinel.
  const radixValue =
    value === ""
      ? hasEmptyOption && !showEmptyAsPlaceholder
        ? EMPTY_VALUE_TOKEN
        : undefined
      : value;

  return (
    <SelectPrimitive.Root
      value={radixValue}
      onValueChange={(v) => onChange(fromRadix(v))}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger
        className={`group inline-flex min-w-[160px] items-center justify-between gap-2 outline-none transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer data-[placeholder]:d ${VARIANT_CLASS[variant]} ${SIZE_CLASS[size]} ${className}`}
      >
        <span className="flex items-center gap-2 min-w-0 flex-1">
          {leadingLabel && (
            <span className="text-[11px] font-semibold tracking-wider d shrink-0">
              {leadingLabel}
            </span>
          )}
          {!showEmptyAsPlaceholder && selected?.icon}
          {selected && !showEmptyAsPlaceholder ? (
            <SelectedLabel label={selected.label} />
          ) : (
            <SelectPrimitive.Value placeholder={placeholder} />
          )}
        </span>
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="size-4 d transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          align={align}
          className="z-[200] flex flex-col min-w-[var(--radix-select-trigger-width)] w-max max-w-[min(22rem,calc(100vw-1rem))] max-h-[260px] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-xl will-change-transform data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=open]:duration-150 data-[state=closed]:duration-100"
        >
          <SelectPrimitive.Viewport asChild>
            <div className="p-1 flex-1 min-h-0 max-h-[260px] overflow-y-auto [scrollbar-width:thin] [scrollbar-color:rgba(100,116,139,0.7)_rgba(148,163,184,0.18)] [&::-webkit-scrollbar]:w-[10px] [&::-webkit-scrollbar-track]:bg-slate-400/15 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:my-1 [&::-webkit-scrollbar-thumb]:bg-slate-400/70 dark:[&::-webkit-scrollbar-thumb]:bg-slate-500/70 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-solid [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:bg-clip-content [&::-webkit-scrollbar-thumb]:hover:bg-slate-500 dark:[&::-webkit-scrollbar-thumb]:hover:bg-slate-400">
              {options.map((opt) => (
                <SelectPrimitive.Item
                  key={opt.value}
                  value={opt.value === "" ? EMPTY_VALUE_TOKEN : opt.value}
                  disabled={opt.disabled}
                  className="group/item relative flex w-full cursor-pointer select-none items-center gap-2.5 rounded-lg px-2.5 py-1.5 pr-8 text-sm text-foreground outline-none transition-colors break-words data-[highlighted]:bg-indigo-50 dark:data-[highlighted]:bg-indigo-500/10 data-[highlighted]:text-indigo-700 dark:data-[highlighted]:text-indigo-300 data-[state=checked]:font-semibold data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed"
                >
                  {opt.icon}
                  <SelectPrimitive.ItemText>{opt.label}</SelectPrimitive.ItemText>
                  <SelectPrimitive.ItemIndicator className="ml-auto flex-shrink-0">
                    <Check className="size-4 text-indigo-600 dark:text-indigo-400" />
                  </SelectPrimitive.ItemIndicator>
                </SelectPrimitive.Item>
              ))}
            </div>
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
